// ============================================================
// hooks/useAsistenciaDiaria.ts
// Orquesta marcar / listar / justificar / actualizar.
// ============================================================

import { useState, useCallback, useEffect, useRef } from 'react';
import { HttpError } from 'utils/httpClient';
import { asistenciaDiariaService } from 'services/asistenciaDiariaService';
import {
  AsistenciaDiariaResponse,
  MarcarAsistenciaDiariaPayload,
  JustificarAsistenciaDiariaPayload,
  ActualizarAsistenciaDiariaPayload,
  EstadoAsistenciaDiaria,
} from 'types/asistenciaDiaria.types';

// ── Helpers de fecha en zona Lima ─────────────────────────────

/** Fecha de hoy en Lima en formato YYYY-MM-DD. */
export function todayLima(): string {
  // es-CA → YYYY-MM-DD
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Lima',
    year:  'numeric',
    month: '2-digit',
    day:   '2-digit',
  }).format(new Date());
}

/** Mensaje amigable para errores conocidos del módulo. */
function mensajeMarcar(err: unknown): string {
  if (err instanceof HttpError) {
    if (err.status === 409) {
      return 'Este estudiante ya tiene asistencia registrada hoy. Use editar para corregir.';
    }
    if (err.status === 403) {
      return 'No tienes permiso para registrar asistencia.';
    }
    return err.message;
  }
  return err instanceof Error ? err.message : 'Error al registrar asistencia';
}

// ── Hook principal ────────────────────────────────────────────

export function useAsistenciaDiaria() {
  const [marcando, setMarcando] = useState(false);
  const [errorMarcar, setErrorMarcar] = useState<string | null>(null);
  const [ultimoRegistro, setUltimoRegistro] = useState<AsistenciaDiariaResponse | null>(null);

  /** Marca asistencia por DNI. Lanza error con mensaje listo para mostrar. */
  const marcar = useCallback(async (payload: MarcarAsistenciaDiariaPayload) => {
    setMarcando(true);
    setErrorMarcar(null);
    try {
      const res = await asistenciaDiariaService.marcar(payload);
      setUltimoRegistro(res);
      return res;
    } catch (err: unknown) {
      const msg = mensajeMarcar(err);
      setErrorMarcar(msg);
      throw new Error(msg);
    } finally {
      setMarcando(false);
    }
  }, []);

  return { marcar, marcando, errorMarcar, ultimoRegistro, setErrorMarcar };
}

// ── Hook de listado por fecha ─────────────────────────────────

export function useAsistenciaDiariaPorFecha(fechaInicial: string = todayLima()) {
  const [fecha, setFecha] = useState(fechaInicial);
  const [registros, setRegistros] = useState<AsistenciaDiariaResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reqRef = useRef(0);

  const cargar = useCallback(async (signal?: AbortSignal) => {
    const id = ++reqRef.current;
    setLoading(true);
    setError(null);
    try {
      const data = await asistenciaDiariaService.listarPorFecha(fecha, signal);
      if (id !== reqRef.current) return;
      setRegistros(data);
    } catch (err: unknown) {
      if (id !== reqRef.current) return;
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Error al cargar asistencias');
    } finally {
      if (id === reqRef.current) setLoading(false);
    }
  }, [fecha]);

  useEffect(() => {
    const ctrl = new AbortController();
    cargar(ctrl.signal);
    return () => ctrl.abort();
  }, [cargar]);

  const actualizarLocal = useCallback((reg: AsistenciaDiariaResponse) => {
    setRegistros(prev => prev.map(r => r.idAsistenciaDiaria === reg.idAsistenciaDiaria ? reg : r));
  }, []);

  return { fecha, setFecha, registros, loading, error, recargar: cargar, actualizarLocal };
}

// ── Hook de historial por matrícula ───────────────────────────

export function useAsistenciaDiariaHistorial(matriculaId: number | null, desde: string, hasta: string) {
  const [registros, setRegistros] = useState<AsistenciaDiariaResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!matriculaId) {
      setRegistros([]);
      return;
    }
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    asistenciaDiariaService.listarPorMatricula(matriculaId, desde, hasta, ctrl.signal)
      .then(setRegistros)
      .catch(err => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Error al cargar el historial');
      })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [matriculaId, desde, hasta]);

  return { registros, loading, error };
}

// ── Acciones de justificar y actualizar ───────────────────────

export function useAccionesAsistenciaDiaria() {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const justificar = useCallback(async (id: number, payload: JustificarAsistenciaDiariaPayload) => {
    setSubmitting(true);
    setError(null);
    try {
      return await asistenciaDiariaService.justificar(id, payload);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error al justificar';
      setError(msg);
      throw new Error(msg);
    } finally {
      setSubmitting(false);
    }
  }, []);

  const actualizar = useCallback(async (id: number, payload: ActualizarAsistenciaDiariaPayload) => {
    setSubmitting(true);
    setError(null);
    try {
      return await asistenciaDiariaService.actualizar(id, payload);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error al actualizar';
      setError(msg);
      throw new Error(msg);
    } finally {
      setSubmitting(false);
    }
  }, []);

  return { justificar, actualizar, submitting, error };
}

// ── Resumen ───────────────────────────────────────────────────

export interface ResumenAsistenciaDiaria {
  total:       number;
  presente:    number;
  tardanza:    number;
  ausente:     number;
  justificado: number;
}

export function calcularResumenDiaria(registros: AsistenciaDiariaResponse[]): ResumenAsistenciaDiaria {
  const r: ResumenAsistenciaDiaria = { total: registros.length, presente: 0, tardanza: 0, ausente: 0, justificado: 0 };
  for (const x of registros) {
    if (x.estado === 'PRESENTE')    r.presente++;
    else if (x.estado === 'TARDANZA')    r.tardanza++;
    else if (x.estado === 'AUSENTE')     r.ausente++;
    else if (x.estado === 'JUSTIFICADO') r.justificado++;
  }
  return r;
}

// Re-export para conveniencia
export type { EstadoAsistenciaDiaria };
